import { savePackageInclusion } from '~/components/Common/Forms/InclusionsAssetForm/utils';

import InclusionsService from '~/services/InclusionsService';

import { isRecord } from '~/utils/isRecord';

interface PackageInclusionSaveFields {
  asset_salesforce_id?: string;
  category_id?: string | null;
  currency?: string;
  id_salesforce_external?: string;
  inclusion_id?: string;
  is_hidden?: boolean;
  lux_plus_tier?: string | null;
  name?: string;
  offer_id?: string;
  opportunity_name?: string;
  package_id: string;
  package_name?: string;
  rrp?: number | null;
  sort_order?: number;
  style?: string | null;
  type?: string;
  unit?: string;
  vendor_salesforce_id?: string;
}

interface PackageInclusionAssignmentDraft extends PackageInclusionSaveFields {
  id?: undefined;
  offer_id: string;
  vendor_salesforce_id: string;
}

interface PersistedPackageInclusionUpdate extends PackageInclusionSaveFields {
  id: string;
  offer_id: string;
  vendor_salesforce_id: string;
}

export type PackageInclusionMutationPayload = PackageInclusionAssignmentDraft | PersistedPackageInclusionUpdate;

type PackageInclusionRecord = Record<string, unknown>;
type FetchedPackageInclusionRecord = PersistedPackageInclusionUpdate & PackageInclusionRecord;
type PackageInclusionAssignments = Map<string, Array<PackageInclusionAssignmentDraft>>;

interface SavePackageInclusionChangesParams {
  inclusionIdToCustomise?: string;
  offerId: string;
  packageInclusionIdsToDelete?: Array<string>;
  packageInclusions: Array<PackageInclusionMutationPayload>;
}

function getString(value: unknown) {
  return value ? String(value) : undefined;
}

function getPackageInclusionRowId(packageInclusion: { id?: unknown }) {
  return getString(packageInclusion.id);
}

export function getSavedInclusionId(response: unknown) {
  const result = isRecord(response) ? response.result : undefined;

  if (!isRecord(result)) {
    return undefined;
  }

  return getString(result.id) ?? (isRecord(result.inclusion) ? getString(result.inclusion.id) : undefined);
}

export function getSavedInclusionMode(response: unknown) {
  const result = isRecord(response) ? response.result : undefined;

  if (!isRecord(result)) {
    return undefined;
  }

  return result.mode === 'updated' || result.mode === 'customised' ? result.mode : undefined;
}

function getPackageInclusionAssignmentKey(packageInclusion: {
  inclusion_id?: unknown;
  name?: unknown;
  package_id?: unknown;
  vendor_salesforce_id?: unknown;
}) {
  const inclusionId = getString(packageInclusion.inclusion_id);
  const name = getString(packageInclusion.name);
  const vendorId = getString(packageInclusion.vendor_salesforce_id);
  const packageId = getString(packageInclusion.package_id);

  // saveInclusionAsset accepts all package assignments for one vendor inclusion at once.
  return (inclusionId || name) && vendorId && packageId ? `${vendorId}:${inclusionId ?? `custom:${name}`}` : undefined;
}

function getPackageInclusionRelationKey(packageInclusion: { inclusion_id?: unknown; package_id?: unknown }) {
  const inclusionId = getString(packageInclusion.inclusion_id);
  const packageId = getString(packageInclusion.package_id);

  return inclusionId && packageId ? `${packageId}:${inclusionId}` : undefined;
}

function getCanonicalFetchedPackageInclusions(packageInclusions: Array<FetchedPackageInclusionRecord>) {
  return packageInclusions.reduce<Map<string, FetchedPackageInclusionRecord>>(
    (canonicalPackageInclusionsByKey, record) => {
      const key = getPackageInclusionRelationKey(record);

      if (!key || canonicalPackageInclusionsByKey.has(key)) {
        return canonicalPackageInclusionsByKey;
      }

      canonicalPackageInclusionsByKey.set(key, record);

      return canonicalPackageInclusionsByKey;
    },
    new Map(),
  );
}

function isPackageInclusionAssignmentDraft(
  packageInclusion: PackageInclusionMutationPayload,
): packageInclusion is PackageInclusionAssignmentDraft {
  return !getPackageInclusionRowId(packageInclusion);
}

function isPersistedPackageInclusionUpdate(
  packageInclusion: PackageInclusionMutationPayload,
): packageInclusion is PersistedPackageInclusionUpdate {
  return !!getPackageInclusionRowId(packageInclusion);
}

function isFetchedPackageInclusionRecord(value: unknown): value is FetchedPackageInclusionRecord {
  return isRecord(value) && !!getPackageInclusionRowId(value) && !!getPackageInclusionRelationKey(value);
}

function getPackageInclusionAssignments(packageInclusions: Array<PackageInclusionMutationPayload>) {
  return packageInclusions.reduce<PackageInclusionAssignments>((assignments, packageInclusion) => {
    if (!isPackageInclusionAssignmentDraft(packageInclusion)) {
      return assignments;
    }

    const key = getPackageInclusionAssignmentKey(packageInclusion);

    if (!key) {
      return assignments;
    }

    assignments.set(key, (assignments.get(key) ?? []).concat(packageInclusion));

    return assignments;
  }, new Map());
}

function buildInclusionPayload(packageInclusion: PackageInclusionAssignmentDraft) {
  return {
    name: getString(packageInclusion.name),
    ...(packageInclusion.category_id ? { category_id: packageInclusion.category_id } : {}),
    ...('currency' in packageInclusion ? { currency: getString(packageInclusion.currency) } : {}),
    is_hidden: packageInclusion.is_hidden ?? false,
    ...('rrp' in packageInclusion ? { rrp: packageInclusion.rrp ?? null } : {}),
    type: getString(packageInclusion.type) || 'Flash',
    ...('unit' in packageInclusion ? { unit: getString(packageInclusion.unit) } : {}),
  };
}

function buildPackagePayload(packageInclusion: PackageInclusionAssignmentDraft) {
  const packageName = getString(packageInclusion.package_name);

  return {
    id_salesforce_external: getString(packageInclusion.package_id),
    name: packageName,
    deal_option_name: packageName,
    offer_name: getString(packageInclusion.opportunity_name),
    offer_id_salesforce_external: getString(packageInclusion.offer_id),
    sort_order: packageInclusion.sort_order,
    style: packageInclusion.style,
    lux_plus_tier: packageInclusion.lux_plus_tier,
  };
}

function buildPackageOverridePayload(packageInclusion: PackageInclusionAssignmentDraft) {
  return {
    package_id: getString(packageInclusion.package_id),
    package_name: getString(packageInclusion.package_name),
    opportunity_name: getString(packageInclusion.opportunity_name),
    sort_order: packageInclusion.sort_order,
    style: packageInclusion.style,
    lux_plus_tier: packageInclusion.lux_plus_tier,
  };
}

async function assignPackageInclusions(packageInclusionAssignments: PackageInclusionAssignments) {
  const assignedPackageInclusions: Array<PackageInclusionAssignmentDraft & { inclusion_id: string }> = [];

  for (const packageInclusionsToAssign of packageInclusionAssignments.values()) {
    const [firstPackageInclusion] = packageInclusionsToAssign;
    const inclusionId = getString(firstPackageInclusion.inclusion_id);
    const vendorId = getString(firstPackageInclusion.vendor_salesforce_id);

    if (!vendorId) {
      continue;
    }

    const response = await InclusionsService.saveInclusionAsset(
      vendorId,
      {
        ...(inclusionId ? { id: inclusionId } : {}),
        ...buildInclusionPayload(firstPackageInclusion),
        vendor_salesforce_id: vendorId,
        packages: packageInclusionsToAssign.map(buildPackagePayload),
      },
      inclusionId,
    );
    const savedInclusionId = inclusionId ?? getSavedInclusionId(response);

    if (!savedInclusionId) {
      throw new Error('Saved inclusion response did not include an inclusion ID.');
    }

    assignedPackageInclusions.push(
      ...packageInclusionsToAssign.map((packageInclusion) => ({
        ...packageInclusion,
        inclusion_id: savedInclusionId,
      })),
    );
  }

  return assignedPackageInclusions;
}

async function customiseInclusionForOffer(
  inclusionId: string,
  packageInclusionAssignments: PackageInclusionAssignments,
) {
  const packageInclusionsToAssign = Array.from(packageInclusionAssignments.values()).flat();
  const [firstPackageInclusion] = packageInclusionsToAssign;
  const vendorId = getString(firstPackageInclusion?.vendor_salesforce_id);
  const offerId = getString(firstPackageInclusion?.offer_id);

  if (packageInclusionsToAssign.length === 0) {
    throw new Error('Unable to customise inclusion: no package inclusions to assign.');
  }

  if (!vendorId || !offerId) {
    throw new Error('Unable to customise inclusion without an offer, vendor and package.');
  }

  return InclusionsService.customiseInclusionForOffer(inclusionId, {
    vendor_salesforce_id: vendorId,
    offer_id: offerId,
    inclusion: buildInclusionPayload(firstPackageInclusion),
    packages: packageInclusionsToAssign.map(buildPackageOverridePayload),
  });
}

function getPackageInclusionsFromResponse(response: unknown) {
  const result = isRecord(response) ? response.result : undefined;

  if (!isRecord(result)) {
    return [];
  }

  return Object.values(result).flatMap((packageInclusions) =>
    Array.isArray(packageInclusions) ? packageInclusions.filter(isFetchedPackageInclusionRecord) : [],
  );
}

async function getAssignedPackageInclusionsToUpdate(
  offerId: string,
  assignedPackageInclusions: Array<PackageInclusionAssignmentDraft & { inclusion_id: string }>,
) {
  if (assignedPackageInclusions.length === 0) {
    return [];
  }

  const fetchedPackageInclusions = getPackageInclusionsFromResponse(
    await InclusionsService.fetchPackageInclusions(offerId),
  );
  const fetchedPackageInclusionsByKey = getCanonicalFetchedPackageInclusions(fetchedPackageInclusions);
  const missingAssignedPackageInclusion = assignedPackageInclusions.find((packageInclusion) => {
    const key = getPackageInclusionRelationKey(packageInclusion);

    return !key || !fetchedPackageInclusionsByKey.has(key);
  });

  if (missingAssignedPackageInclusion) {
    throw new Error('Unable to save package inclusion metadata after assigning packages.');
  }

  return assignedPackageInclusions.flatMap((packageInclusion) => {
    const key = getPackageInclusionRelationKey(packageInclusion);
    const fetchedPackageInclusion = key ? fetchedPackageInclusionsByKey.get(key) : undefined;

    if (!fetchedPackageInclusion) {
      return [];
    }

    const rowId = getPackageInclusionRowId(fetchedPackageInclusion);

    return rowId ? [{ ...fetchedPackageInclusion, ...packageInclusion, id: rowId }] : [];
  });
}

export async function savePackageInclusionChanges({
  inclusionIdToCustomise,
  offerId,
  packageInclusionIdsToDelete = [],
  packageInclusions,
}: SavePackageInclusionChangesParams) {
  const packageInclusionAssignments = getPackageInclusionAssignments(packageInclusions);

  if (inclusionIdToCustomise) {
    return customiseInclusionForOffer(inclusionIdToCustomise, packageInclusionAssignments);
  }

  if (packageInclusionIdsToDelete.length > 0) {
    await InclusionsService.unlinkPackagesByInclusionIds({ ids: packageInclusionIdsToDelete });
  }

  const assignedPackageInclusions = await assignPackageInclusions(packageInclusionAssignments);

  const packageInclusionsToUpdate = packageInclusions
    .filter(isPersistedPackageInclusionUpdate)
    .concat(await getAssignedPackageInclusionsToUpdate(offerId, assignedPackageInclusions));

  if (packageInclusionsToUpdate.length > 0) {
    return InclusionsService.updatePackageInclusion({
      package_inclusions: packageInclusionsToUpdate.map((packageInclusion) =>
        savePackageInclusion(packageInclusion, undefined),
      ),
    });
  }
}
